import { Box, Typography, LinearProgress, Paper, Divider } from '@mui/material';
import MessageFormatter from '../MessageFormatter';

/**
 * AdoptionBreakdownSection - Per-dimension breakdown with insights
 *
 * For organization/industry reports only (not personal).
 * Displays each dimension with:
 * - Dimension name + score (X.X/5.0) + wide progress bar
 * - Description directly under header
 * - Cached insights (if available from AI Evaluation)
 *
 * TODO: Add "How to improve this?" button - see GitHub Issue #35
 */

// Static descriptions for each dimension
const DIMENSION_DESCRIPTIONS = {
  strategy_capability: 'Alignment of AI initiatives with business goals, building tech/data foundations, and implementing responsible adoption frameworks.',
  infrastructure_data: 'Data quality, accessibility, governance, and computing infrastructure readiness for AI workloads.',
  initiatives_operations: 'Active AI projects, operational integration, tool adoption patterns, and process automation.',
  value_realisation: 'Measurable business outcomes, ROI tracking, and value delivery from AI implementations.'
};

// Map dimension IDs to display names
const DIMENSION_NAMES = {
  strategy_capability: 'Strategy & Capability',
  infrastructure_data: 'Infrastructure & Data',
  initiatives_operations: 'Initiatives & Operations',
  value_realisation: 'Value Realisation'
};

// Progress bar color based on score
const getProgressColor = (score, hasData) => {
  if (!hasData) return '#e0e0e0';
  if (score >= 3.5) return '#22C55E'; // Green
  if (score >= 2.0) return '#F59E0B'; // Orange
  return '#EF4444'; // Red
};

export default function AdoptionBreakdownSection({
  dimensionSummaries,
  dimensionInsights = {},
  dimensionMapping = {}
}) {
  if (!dimensionSummaries || dimensionSummaries.length === 0) {
    return null;
  }

  return (
    <Paper elevation={0} sx={{ backgroundColor: 'transparent' }}>
      <Typography
        variant="h5"
        sx={{
          fontFamily: 'Bebas Neue',
          fontWeight: 'normal',
          color: 'rgb(1, 28, 45)',
          textAlign: 'center',
          mb: 3,
          letterSpacing: 1
        }}
      >
        AI ADOPTION STATUS BREAKDOWN
      </Typography>

      {dimensionSummaries.map((summary, index) => {
        const hasData = summary.axisCount > 0;
        const dimensionId = summary.dimension;
        const displayName = dimensionMapping[dimensionId] || DIMENSION_NAMES[dimensionId] || dimensionId;
        const description = DIMENSION_DESCRIPTIONS[dimensionId] || '';
        const insight = dimensionInsights[dimensionId];

        return (
          <Box key={dimensionId}>
            {index > 0 && <Divider sx={{ my: 3 }} />}

            <Box sx={{ py: 2 }}>
              {/* Header Row: Dimension Name + Score + Wide Progress Bar */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: hasData ? 'rgb(1, 28, 45)' : '#999',
                    fontSize: '1rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    flexShrink: 0
                  }}
                >
                  {displayName}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, justifyContent: 'flex-end' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      color: hasData ? 'rgb(1, 28, 45)' : '#999',
                      fontSize: '1.1rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {hasData ? `${summary.avgScore.toFixed(1)}/5.0` : '-/5.0'}
                  </Typography>

                  {/* Wide, thick progress bar */}
                  <Box sx={{ width: '45%', maxWidth: 350, minWidth: 200 }}>
                    <LinearProgress
                      variant="determinate"
                      value={hasData ? (summary.avgScore / 5) * 100 : 0}
                      sx={{
                        height: 20,
                        borderRadius: 10,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getProgressColor(summary.avgScore, hasData),
                          borderRadius: 10,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Description directly under header */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, fontSize: '0.85rem', fontStyle: 'italic' }}
              >
                {description}
              </Typography>

              {/* Key Insights - only show if cached insight exists */}
              {insight && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      color: 'rgb(1, 28, 45)',
                      mb: 1,
                      fontSize: '0.85rem'
                    }}
                  >
                    Key insights:
                  </Typography>
                  <Box
                    sx={{
                      pl: 2,
                      borderLeft: '3px solid rgba(1, 28, 45, 0.2)',
                      '& p': { margin: '0.5em 0', fontSize: '0.9rem', lineHeight: 1.6 },
                      '& ul, & ol': { margin: '0.5em 0', paddingLeft: '1.5em' },
                      '& li': { fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '0.25em' }
                    }}
                  >
                    <MessageFormatter content={insight} />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
}
